import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import LazyImage from '../components/LazyImage';
import image2 from '../images/barvinokvonka1.png';
import alternativeHero from '../images/alternative.jpg';

const Contact = () => {
  const { t } = useLanguage();

  const [heroRef, heroVisible] = useScrollAnimation({ threshold: 0.2 });
  const [mapRef, mapVisible] = useScrollAnimation();

  return (
    <>
      <Helmet>
        <title>Kontakt – Chata Barvinok Litmanová</title>
        <meta
          name="description"
          content="Kontaktujte chatu Barvinok v Litmanovej – telefón, e‑mail, adresa a mapa s presnou polohou pri lyžiarskom stredisku."
        />
      </Helmet>
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className={`relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-24 pb-16 transition-all duration-800 ${heroVisible ? 'animate-fade-in opacity-100' : 'opacity-0'}`}
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <LazyImage 
            src={image2} 
            lowQualitySrc={alternativeHero} // pre pomalé pripojenia použijeme alternative.jpg
            alt={t('home.contactTitle')}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className={`text-center mb-12 transition-all duration-800 delay-200 ${heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}>
            <span className="text-copper-400 text-sm font-semibold uppercase tracking-widest mb-4 block">
              {t('home.contact')}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('home.contactTitle')}
            </h1>
            <div className="w-24 h-1 bg-copper-400 mx-auto mb-4"></div>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto text-lg">
              {t('home.contactSubtitle')}
            </p>
          </div>

          {/* Contact Cards */}
          <div className={`grid md:grid-cols-3 gap-6 mb-8 transition-all duration-800 delay-300 ${heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'}`}>
            {/* Phone Card */}
            <a 
              href="tel:+421950754495"
              className="text-center p-8 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-gray-700 hover:border-copper-500 transition-colors cursor-pointer block"
            >
              <div className="w-16 h-16 bg-copper-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">{t('home.phone')}</h3>
              <p className="text-gray-600 dark:text-gray-400">+421 950 754 495</p>
            </a>

            {/* Email Card */}
            <a 
              href="mailto:chatabarvinok@gmail.com"
              className="text-center p-8 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-gray-700 hover:border-copper-500 transition-colors cursor-pointer block"
            >
              <div className="w-16 h-16 bg-copper-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">{t('home.email')}</h3>
              <p className="text-gray-600 dark:text-gray-400">chatabarvinok@gmail.com</p>
            </a>

            {/* Address Card */}
            <a 
              href="https://www.google.com/maps/search/?api=1&query=49.377184,20.622115"
              target="_blank"
              rel="noopener noreferrer"
              className="text-center p-8 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-gray-700 hover:border-copper-500 transition-colors cursor-pointer block"
            >
              <div className="w-16 h-16 bg-copper-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">{t('home.address')}</h3>
              <p className="text-gray-600 dark:text-gray-400">065 31 Litmanová 328</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Slovensko</p>
            </a>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div 
              ref={mapRef}
              className={`bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-300 dark:border-gray-700 overflow-hidden shadow-2xl transition-all duration-800 delay-200 ${mapVisible ? 'animate-scale-in opacity-100' : 'opacity-0'}`}
            >
              <div className="p-6 border-b border-gray-300 dark:border-gray-700">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('contact.mapTitle')}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{t('contact.mapSubtitle')}</p>
              </div>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={`https://www.google.com/maps?q=${49.37718418972593},${20.62211495767094}&hl=sk&z=17&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ position: 'absolute', top: 0, left: 0, border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Chata Barvinok Location"
                  className="w-full h-full"
                ></iframe>
              </div>
              <div className="p-6 bg-gray-200/50 dark:bg-gray-800/50">
                <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-copper-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span>GPS: 49.377184, 20.622115</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-copper-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span>065 31 Litmanová 328, Slovensko</span>
                  </div>
                  <a 
                    href="https://www.google.com/maps/place/Chata+Barvinok/@49.3771318,20.6220935,17z/data=!4m14!1m7!3m6!1s0x473e0f0043ac969f:0x15f413f8d84ac1fe!2sChata+Barvinok!8m2!3d49.3771318!4d20.6220935!16s%2Fg%2F11yr50pbz6!3m5!1s0x473e0f0043ac969f:0x15f413f8d84ac1fe!8m2!3d49.3771318!4d20.6220935!16s%2Fg%2F11yr50pbz6?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoASAFQAw%3D%3D"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-copper-400 hover:text-copper-300 transition-colors flex items-center space-x-1"
                  >
                    <span>{t('contact.openInMaps')}</span>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Contact;

